// ============================================================================
// Anaglyphic, Amsterdam 2023, All Rights Reserved.
// See README in the root project for more information.
// ============================================================================

import { defineStore } from "pinia";
import { v4 as uuidv4 } from "uuid";

// ============================================================================

export type ToastType = "info" | "warning" | "error" | "loading" | "success";
export type ToastPosition =
	| "top-right"
	| "top-left"
	| "bottom-right"
	| "bottom-left"
	| "top-center"
	| "bottom-center";

export interface Toast {
	id: string;
	type: ToastType;
	message: string;
	title?: string;
	timeout?: number;
	position?: ToastPosition;
	dismissible?: boolean;
	onDismiss?: () => void;
	onClick?: () => void;
	progress?: number;
}

export type ToastOptions = Omit<Toast, "id" | "type" | "message"> & {
	id?: string;
};

// ============================================================================

export const useToastStore = defineStore("toast", {
	state: () => ({
		toasts: [] as Toast[],
		defaultPosition: "bottom-right" as ToastPosition,
	}),

	actions: {
		add(toast: Toast) {
			const existingToastIndex = this.toasts.findIndex(
				(t) => t.id === toast.id
			);
			if (existingToastIndex !== -1) {
				return toast.id;
			}

			if (!toast.position) {
				toast.position = this.defaultPosition;
			}

			this.toasts.push(toast);
			if (toast.timeout !== undefined) {
				setTimeout(() => {
					this.remove(toast.id);
				}, toast.timeout);
			}

			return toast.id;
		},
		remove(id: string) {
			const index = this.toasts.findIndex((t) => t.id === id);
			if (index !== -1) {
				const toast = this.toasts[index];
				if (toast.onDismiss) {
					toast.onDismiss();
				}
				this.toasts.splice(index, 1);
			}
		},
		update(id: string, updates: Partial<Toast>) {
			const toast = this.toasts.find((t) => t.id === id);
			if (!toast) return;
			Object.assign(toast, updates);

			if (
				toast.type !== "loading" &&
				updates.type &&
				updates.type !== "loading"
			) {
				setTimeout(() => {
					this.remove(id);
				}, toast.timeout || 5000);
			}
		},
		clearById(id) {
			const t = this.toasts.find((t) => t.id === id);
			if (!t) {
				return;
			}

			if (t.onDismiss) {
				t.onDismiss();
			}

			this.toasts = this.toasts.filter((t) => t.id !== id);
		},
		clear(typesToClear?: ToastType | ToastType[]) {
			if (typesToClear === undefined) {
				this.toasts.forEach((toast) => {
					if (toast.onDismiss) {
						toast.onDismiss();
					}
				});
				this.toasts = [];
				return;
			}

			const typesArray = Array.isArray(typesToClear)
				? typesToClear
				: [typesToClear];
			const toastsToRemove = this.toasts.filter((toast) =>
				typesArray.includes(toast.type)
			);
			toastsToRemove.forEach((toast) => {
				if (toast.onDismiss) {
					toast.onDismiss();
				}
			});

			this.toasts = this.toasts.filter(
				(toast) => !typesArray.includes(toast.type)
			);
		},
		info(message: string, options?: ToastOptions) {
			const id = options?.id || uuidv4();
			if (options?.id && this.toasts.some((t) => t.id === id)) {
				return id;
			}

			this.add({
				id,
				type: "info",
				message,
				dismissible: true,
				...options,
			});
			return id;
		},
		warning(message: string, options?: ToastOptions) {
			const id = options?.id || uuidv4();
			if (options?.id && this.toasts.some((t) => t.id === id)) {
				return id;
			}

			this.add({
				id,
				type: "warning",
				message,
				dismissible: true,
				...options,
			});
			return id;
		},
		error(message: string, options?: ToastOptions) {
			const id = options?.id || uuidv4();
			if (options?.id && this.toasts.some((t) => t.id === id)) {
				return id;
			}

			this.add({
				id,
				type: "error",
				message,
				dismissible: true,
				...options,
			});
			return id;
		},
		loading(message: string, options?: ToastOptions) {
			const id = options?.id || uuidv4();
			if (options?.id && this.toasts.some((t) => t.id === id)) {
				return id;
			}

			this.add({
				id,
				type: "loading",
				message,
				dismissible: false,
				...options,
			});
			return id;
		},
		success(message: string, options?: ToastOptions) {
			const id = options?.id || uuidv4();
			if (options?.id && this.toasts.some((t) => t.id === id)) {
				return id;
			}

			this.add({
				id,
				type: "success",
				message,
				dismissible: true,
				...options,
			});
			return id;
		},
	},
});
